import { useEffect, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Button, Stack } from "@salesapp/ui";
import { TopNav } from "./TopNav";
import { useAccess } from "./AccessProvider";
import { useAuth } from "./AuthProvider";
import { useLocale } from "./LocaleProvider";
import { billingPath } from "../lib/billingApi";
import { LoxleyAssistant } from "../loxley/LoxleyAssistant";
import { LoxleyProvider } from "../loxley/LoxleyProvider";
import "./shell.css";

/** Matches /settings and /apps/crm/{anything}/settings — the org-level settings screen, slugged or not. */
function onSettingsScreen(pathname: string): boolean {
  if (pathname === "/settings") return true;
  const parts = pathname.split("/").filter(Boolean);
  return parts[0] === "apps" && parts[1] === "crm" && parts[parts.length - 1] === "settings";
}

function onBillingScreen(pathname: string, search: string): boolean {
  if (pathname === "/billing") return true;
  if (!onSettingsScreen(pathname)) return false;
  return new URLSearchParams(search).get("tab") === "billing";
}

/** Top nav + content well. No left rail — the structural difference from a dense GRC console. */
export function AppShell({ children }: { children: ReactNode }) {
  const { showExpiryBanner, daysRemaining, accessEndsAt, accessBlocked, isTrial, grantSource } = useAccess();
  const paymentPending = grantSource === "pending_provider";
  // A partner (billing-exempt) licence cannot be paid for, so its admin is not sent to billing; staff extend it.
  const partnerLicence = grantSource === "billing_exempt";
  const canPayToRestore = !partnerLicence;
  const { session } = useAuth();
  const { formatDateTime } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const billingOpen = onBillingScreen(location.pathname, location.search);
  const billingHref = billingPath(session?.orgSlug);

  useEffect(() => {
    if (accessBlocked && session?.isOrgAdmin && !billingOpen && canPayToRestore) {
      navigate(billingHref, { replace: true });
    }
  }, [accessBlocked, session?.isOrgAdmin, billingOpen, navigate, canPayToRestore, billingHref]);

  const daysLabel = `${daysRemaining} day${daysRemaining === 1 ? "" : "s"}`;
  const endsWhen = accessEndsAt ? ` (${formatDateTime(accessEndsAt)})` : "";

  return (
    <LoxleyProvider>
    <div className="app-shell-frame">
      <TopNav />
      {accessBlocked ? (
        <div className="lifecycle-banner" role="alert">
          {paymentPending
            ? "Payment pending. CRM is paused until your payment is confirmed."
            : isTrial
              ? "Trial ended. CRM is paused until you choose a plan."
              : partnerLicence
                ? "Partner access ended. CRM is paused until Saleslox extends it."
                : "Your subscription has expired. Please pay for a subscription to enable the apps."}
        </div>
      ) : showExpiryBanner ? (
        <div className="lifecycle-banner" role="status">
          {isTrial ? (
            <>
              Trial ends in {daysLabel}{endsWhen}.{" "}
              <Link to={billingHref}>Choose a plan</Link>
            </>
          ) : (
            <>Access ends in {daysLabel}{endsWhen}.</>
          )}
        </div>
      ) : null}
      <main className="app-shell-main">
        {accessBlocked && !billingOpen ? (
          <Stack gap={4} className="trial-gate">
            <h1>{paymentPending ? "Payment pending" : isTrial ? "Trial ended" : partnerLicence ? "Access ended" : "Subscription expired"}</h1>
            <p>
              {paymentPending
                ? session?.isOrgAdmin
                  ? "CRM is locked until your payment is confirmed. If you have already paid, this can take a few minutes. You can check or complete payment on the billing screen."
                  : "CRM is locked until your organization's payment is confirmed. Ask an organization admin to check billing."
                : isTrial
                  ? session?.isOrgAdmin
                    ? "CRM is locked until you upgrade or change plan. Card details are entered on the billing screen."
                    : "CRM is locked until an organization admin chooses a plan."
                  : partnerLicence
                    ? "Your partner licence has ended. Contact Saleslox to extend it."
                    : session?.isOrgAdmin
                      ? "Your subscription has expired. Please pay for a subscription to enable the apps. Choose a plan on the billing screen."
                      : "Your organization's subscription has expired. Ask an organization admin to pay for a subscription to enable the apps."}
            </p>
            {canPayToRestore && session?.isOrgAdmin ? (
              <Button type="button" onClick={() => navigate(billingHref)}>
                Open billing
              </Button>
            ) : null}
          </Stack>
        ) : (
          children
        )}
      </main>
    </div>
    {/* Loxley stays out of the way while the CRM is locked for billing. */}
    {accessBlocked ? null : <LoxleyAssistant />}
    </LoxleyProvider>
  );
}
